import * as React from 'react'
import styled from 'styled-components'

import * as behaviours from '../behaviours'
import * as glyphs from '../icons/glyphs'
import * as S from '../styles'
import * as imageUploader from '../styles/imageUploader'

import { Spinner } from './Spinner'
import { Tooltip } from './Tooltip'

export type ImageUploaderProps = Omit<React.LabelHTMLAttributes<HTMLLabelElement>, 'onChange'> & {
  className?: string
  disabled?: boolean
  error?: string
  id?: string
  loading?: boolean
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  onDrop: (event: React.DragEvent<HTMLLabelElement>) => void
  onRemove: (event: React.MouseEvent<HTMLButtonElement>) => void
  previewSrc?: string
  removalDisabled?: boolean
  translations: {
    downloadImage: string
    fileFormats: string
    fileSizeLimit?: string
    removalDisabledWarning?: string
    removeImage: string
    slideItHere: string
  }
} & Omit<imageUploader.WrapperProps, '$active' | '$errored' | '$loading' | 'disabled'>

function ImageUploaderComponent({
  className,
  disabled = false,
  error,
  id,
  loading = false,
  onChange,
  onDrop,
  onRemove,
  previewSrc,
  removalDisabled = false,
  translations,
  ...labelProps
}: ImageUploaderProps): React.ReactElement<ImageUploaderProps> {
  const [active, setAsActive] = React.useState(false)
  const { classList } = behaviours.getStatusClassName(className, error)

  const stableId = React.useId()

  return (
    <>
      <S.imageUploader.Wrapper
        $active={active}
        className={classList.filter(Boolean).join(' ')}
        disabled={disabled}
        $errored={Boolean(error)}
        htmlFor={id ?? stableId}
        $loading={loading}
        onDragLeave={(event) => {
          event.preventDefault()
          setAsActive(false)
        }}
        onDragOver={(event) => {
          event.preventDefault()
          setAsActive(true)
        }}
        onDrop={(event) => {
          event.preventDefault()
          setAsActive(false)
          onDrop(event)
        }}
        {...labelProps}
      >
        <Spinner small />
        <S.imageUploader.Preview $visible={!!previewSrc}>
          {previewSrc && (
            <>
              <img alt="" src={previewSrc} />
              <S.imageUploader.RemoveImage
                type="button"
                disabled={removalDisabled}
                onClick={(event) => {
                  if (!removalDisabled) {
                    /* As this button is wrapped in a <label>, we secure its onClick event by preventing event propagation */
                    event.preventDefault()
                    onRemove(event)
                  }
                }}
              >
                <glyphs.stroke.Trash size={15} />
                <S.copy.XS>{translations.removeImage}</S.copy.XS>
                <Tooltip open={true} position="bottom">
                  <S.copy.S>{translations.removalDisabledWarning}</S.copy.S>
                </Tooltip>
              </S.imageUploader.RemoveImage>
            </>
          )}
        </S.imageUploader.Preview>
        <S.imageUploader.Explanations>
          <S.imageUploader.RoundedIcon>
            <glyphs.stroke.ArrowRight size={20} />
          </S.imageUploader.RoundedIcon>
          <S.heading.XXXS>{translations.downloadImage}</S.heading.XXXS>
          <S.imageUploader.MidText>{translations.slideItHere}</S.imageUploader.MidText>
          <span>
            <S.imageUploader.SmallText as="span">{translations.fileFormats}</S.imageUploader.SmallText>
            {translations.fileSizeLimit && (
              <S.imageUploader.SmallText as="span">
                {'\u00A0'}— {translations.fileSizeLimit}
              </S.imageUploader.SmallText>
            )}
          </span>
        </S.imageUploader.Explanations>
        <S.imageUploader.InvisibleInput
          disabled={disabled || loading}
          accept="image/*"
          type="file"
          id={id ?? stableId}
          onChange={onChange}
        />
      </S.imageUploader.Wrapper>
      {error && <S.imageUploader.ErrorMessage>{error}</S.imageUploader.ErrorMessage>}
    </>
  )
}

export const ImageUploader = styled(ImageUploaderComponent)<ImageUploaderProps>``
ImageUploader.displayName = 'ImageUploader'
ImageUploader.defaultProps = {}
